"use client";
import {
  Badge,
  Dot,
  Heading,
  Text,
  Logo,
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@xenithlabs/xenith-ui";
import { StagingOnly } from "@repo/utils/components/StagingOnly";
import { DevOnly } from "@repo/utils/components/DevOnly";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { MagicSent } from "./MagicSent";
import { CreateMagicLinkLoginMutationRequest } from "@repo/contract";
import { z } from "zod";

import { toast } from "@repo/utils/toast";
const schema = z.object({
  email: z
    .string({
      message: "Email should not be empty",
    })
    .email({
      message: "Email should be a valid email",
    }),
});

type FormSchema = z.infer<typeof schema>;

export function LoginPageMagicLink() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const form = useForm<FormSchema>();
  const onSubmit = (values: FormSchema) => {
    const onSuccess = () => {
      setSent(true);
    };
    const onFailed = () => {
      toast({
        description: "Failed to send magic link",
      });
    };

    setIsSubmitting(true);
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/v1/auth/magic-link/operator`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: values.email,
      } satisfies CreateMagicLinkLoginMutationRequest),
    })
      .then(onSuccess)
      .catch(onFailed)
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const email = form.watch("email");

  if (sent) {
    return <MagicSent email={email} />;
  }
  return (
    <div className="flex items-center justify-center w-screen h-screen">
      <Logo className="absolute top-4 left-4" />

      <DevOnly fallback={<div />}>
        <div className="absolute left-[156px] top-[18px]">
          <Badge theme="neutral" size="sm">
            <Dot className="bg-bg-warning" />
            <div>DEV</div>
          </Badge>
        </div>
      </DevOnly>
      <StagingOnly fallback={<div />}>
        <div className="absolute left-[156px] top-[18px]">
          <Badge theme="neutral" size="sm">
            <Dot className="bg-bg-warning" />
            <div>STAGING</div>
          </Badge>
        </div>
      </StagingOnly>

      <div className="grid w-[400px]">
        <Heading variant="h2">Welcome back!</Heading>
        <Text variant="subtitle" className="mt-2 mb-6 text-text-weak">
          Enter your email to login
        </Text>

        <div className="w-[400px]">
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button loading={isSubmitting} className="w-full mt-6">
                Login
              </Button>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}
